import { useEffect, type ReactNode, type Ref } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/cn";
import { EASE_OUT_PANEL, FADE_BACKDROP, SPRING_MODAL } from "@/lib/motion";
import { resetZoom } from "@/lib/zoomReset";

export type ModalAlign = "center" | "sheet" | "top";
export type ModalMotionStyle = "pop" | "rise" | "fade";

export interface ModalShellProps {
  /** Close handler for clicks on the backdrop (panel clicks are swallowed). */
  onBackdropClick?: () => void;
  /**
   * `center` — classic dialog. `sheet` — bottom sheet on phones, centered on
   * sm+. `top` — top-aligned with a scrollable backdrop (long operator forms).
   */
  align?: ModalAlign;
  /**
   * `pop` — spring with y+scale (default; plain panels). `rise` — spring,
   * opacity+y only. `fade` — opacity only. Panels that clip an image edge
   * (hero galleries, offer photos) must use `rise`/`fade`: scaling the
   * clipped container re-rasterizes the image edge and flashes a seam.
   */
  motionStyle?: ModalMotionStyle;
  /** Stacking context, e.g. "z-[120]". Default z-[105]. */
  zClass?: string;
  /** Extra backdrop classes (padding tweaks) — merged last, so they win. */
  backdropClass?: string;
  /** Panel sizing/layout classes (max-w, max-h, overflow, padding). */
  panelClass?: string;
  panelRef?: Ref<HTMLDivElement>;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  children: ReactNode;
}

/**
 * The one modal surface: unified backdrop, border, shadow, and radius for
 * every overlay dialog in the app (operator and consumer used to speak two
 * different modal languages). Render it INSIDE an `<AnimatePresence>` under
 * the caller's open condition; Escape handling and body scroll-lock stay
 * with the caller.
 *
 * Honors prefers-reduced-motion by collapsing every style to a plain fade.
 */
export function ModalShell({
  onBackdropClick,
  align = "center",
  motionStyle = "pop",
  zClass,
  backdropClass,
  panelClass,
  panelRef,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  children,
}: ModalShellProps) {
  const reduceMotion = useReducedMotion();
  const style = reduceMotion ? "fade" : motionStyle;

  // Mounted = opened: a page left pinch-zoomed would put the panel off-screen.
  useEffect(resetZoom, []);

  // `animate` must not mention y/scale in fade mode: putting them under
  // framer's transform management (even at identity) can re-rasterize a panel
  // that clips an image edge — the exact seam flash fade exists to avoid.
  const panelMotion =
    style === "fade"
      ? {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: EASE_OUT_PANEL,
        }
      : style === "rise"
        ? {
            initial: { opacity: 0, y: 16 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: 10 },
            transition: SPRING_MODAL,
          }
        : {
            initial: { opacity: 0, y: 12, scale: 0.97 },
            animate: { opacity: 1, y: 0, scale: 1 },
            exit: { opacity: 0, y: 8, scale: 0.97 },
            transition: SPRING_MODAL,
          };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={FADE_BACKDROP}
      onClick={onBackdropClick}
      className={cn(
        "fixed inset-0 flex justify-center bg-black/70 light:bg-black/30 backdrop-blur-sm",
        align === "sheet" && "items-end p-0 sm:items-center sm:p-4",
        align === "top" && "items-start overflow-y-auto p-3 sm:p-6",
        align === "center" && "items-center p-3 sm:p-4",
        zClass ?? "z-[105]",
        backdropClass,
      )}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      <motion.div
        ref={panelRef}
        {...panelMotion}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative w-full border border-white/15 light:border-black/15 bg-zinc-950 light:bg-white shadow-[0_0_0_1px_#000,0_24px_48px_-28px_#000] light:shadow-[0_24px_48px_-28px_rgba(0,0,0,0.35)]",
          align === "sheet" ? "rounded-t-3xl sm:rounded-3xl" : "rounded-2xl sm:rounded-3xl",
          panelClass,
        )}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
